/**
 * Widget Home (docs/scope/WORKFLOWS_RUNTIME_CONTRACT.md section 6): a few options shown when the
 * widget opens, before anyone types. Stored as `content.home` of the widget draft / published
 * config; the public widget config returns it already filtered for the visitor. Limits mirror the
 * backend widget schema.
 */

export const WIDGET_HOME_LIMITS = {
  /** Options on the Home screen. */
  options: 6,
  label: 40,
  description: 120,
  greeting: 80,
  subtitle: 120,
  /** Messenger greeting text (Facebook's own cap for the Get Started greeting). */
  messengerGreeting: 160,
  /** The question an option sends as a chat message. */
  question: 200,
  url: 2048,
  /** Option ids are generated; this is the longest the server accepts. */
  optionId: 40,
} as const;

/** What a tap does. */
export const HOME_ACTIONS = ["workflow", "booking", "question", "link", "person"] as const;
export type HomeAction = (typeof HOME_ACTIONS)[number];

/** Who sees Home (`show_to`) or one option (`audience`). */
export const HOME_AUDIENCES = ["everyone", "new_visitors", "returning_visitors"] as const;
export type HomeAudience = (typeof HOME_AUDIENCES)[number];
export const HOME_DEFAULT_AUDIENCE: HomeAudience = "everyone";

/**
 * Icons an option can use (lucide names, present in the console icon set and in the widget's
 * generated icon markup).
 */
export const HOME_ICONS = [
  "message-circle",
  "graduation-cap",
  "wallet",
  "calendar",
  "download",
  "headset",
  "file-text",
  "info",
  "phone",
  "mail",
  "map-pin",
  "star",
  "life-buoy",
  "book-open",
  "megaphone",
  "globe",
] as const;
export type HomeIconName = (typeof HOME_ICONS)[number];
export const HOME_DEFAULT_ICON: HomeIconName = "message-circle";

/** `POST /public/v1/widget/home-events` `event`. */
export const HOME_EVENTS = ["impression", "tap"] as const;
export type HomeEventName = (typeof HOME_EVENTS)[number];

/** Messenger's persistent menu carries the first options only (Messenger Profile API allows 3 top-level items). */
export const HOME_MESSENGER_MENU_ITEMS = 3;

/** Window of the Home analytics footer in the console (`days` of the stats request). */
export const HOME_STATS_DAYS = 30;

/** Option id the server keeps for the Messenger Get Started button. */
export const HOME_RESERVED_OPTION_ID = "start";

/** Links open only over https. */
export const HOME_LINK_PATTERN = /^https:\/\/\S+$/;

/** `GET /v1/agents/{id}/widget/home/stats` */
export interface HomeStats {
  impressions: number;
  /** Taps per option id. */
  taps: Record<string, number>;
  /** Conversations that started from Home. */
  conversations: number;
}
